/**
 * WorkspaceContext.js — RC 1.30.B0 workspace-access state.
 *
 * Fetches `/api/auth/me/access` once after login and exposes the
 * derived permissions to the rest of the app. Provides:
 *
 *   access              — the raw payload from the endpoint
 *   allowedWorkspaces   — string[] of ('admin' | 'candidate' | 'employer')
 *   effectiveDefault    — string or null
 *   currentWorkspace    — derived from the URL path
 *   switchWorkspace(w)  — navigate + auto-persist the new default
 *
 * Auto-persist behaviour: whenever `switchWorkspace(next)` runs and
 * `next` differs from the stored `default_workspace`, we POST it to
 * `/api/auth/me/default-workspace` so the next login lands in the
 * same place. Per product decision: "Persist automatically."
 *
 * Gracefully handles the un-migrated state (migration 025 not yet
 * applied) — the backend already synthesizes a sensible payload so
 * the UI stays functional.
 */
import {
  createContext, useCallback, useContext, useEffect,
  useMemo, useRef, useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { apiFetch } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { hasEmployerInviteBypassFor } from '@/lib/employerInviteBypass';

export const WORKSPACES = ['admin', 'candidate', 'employer'];

/** Route prefixes that identify each workspace from the URL path. */
const WORKSPACE_ROUTES = {
  admin: {
    landing: '/admin/jobs',
    prefixes: ['/admin'],
  },
  employer: {
    landing: '/employer',
    prefixes: ['/employer'],
  },
  candidate: {
    landing: '/dashboard',
    prefixes: [
      '/dashboard', '/jobs', '/documents', '/applications',
      '/account', '/automation',
    ],
  },
};

/** Return the workspace whose route prefixes match `pathname`, or
 *  `fallback` when none match. */
export function workspaceFromPath(pathname, fallback = null) {
  if (typeof pathname !== 'string') return fallback;
  for (const ws of ['admin', 'employer', 'candidate']) {
    for (const p of WORKSPACE_ROUTES[ws].prefixes) {
      if (pathname === p || pathname.startsWith(`${p}/`)) return ws;
    }
  }
  return fallback;
}

export function landingPathFor(workspace) {
  return WORKSPACE_ROUTES[workspace]?.landing || '/dashboard';
}

const WorkspaceContext = createContext(null);

export function WorkspaceProvider({ children }) {
  const { user, hasProfile } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [access, setAccess] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Guard against persisting twice in StrictMode double-invokes and
  // re-renders that fire on the same switch.
  const lastPersistedRef = useRef(null);

  const reload = useCallback(async () => {
    if (!user) {
      setAccess(null);
      return;
    }
    // Phase 0.7.2 — employer-invitee onboarding bypass.
    // A fresh invitee has no `profiles` row, so `/api/auth/me/access`
    // returns the un-migrated "candidate only" fallback shape. While
    // the bypass marker is active for this user, synthesize the
    // employer-only access payload locally so `WorkspaceGuard` lets
    // them through to `/employer/*`. This is purely client-side and
    // route-scoped — it does not weaken any backend authority gate;
    // the FastAPI shim still derives identity from the JWT and the
    // RPC checks A1 authority server-side.
    if (!hasProfile && hasEmployerInviteBypassFor(user.id)) {
      setAccess({
        allowed_workspaces: ['employer'],
        effective_default:  'employer',
        candidate_access:   false,
        employer_access:    true,
        is_admin:           false,
        synthetic:          'emp_invite_bypass',
      });
      setLoading(false);
      return;
    }
    if (!hasProfile) {
      setAccess(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch('/api/auth/me/access');
      setAccess(data || null);
    } catch (err) {
      setError(err?.message || 'Failed to load workspace access.');
      setAccess(null);
    } finally {
      setLoading(false);
    }
  }, [user, hasProfile]);

  useEffect(() => { reload(); }, [reload]);

  /** Non-blocking safety net (RC 1.30.B0 post-review): auto-refresh
   *  the access payload when the tab becomes visible again. Covers
   *  the "admin just granted me employer access and I'm still on
   *  my old session" case — the switcher appears after the next
   *  tab focus without requiring the user to log out and back in.
   *
   *  RC 1.49.x (2026-05-07) — operator-locked throttle. Without a
   *  floor, every micro-tab-switch (Discord ping, Slack notification,
   *  iOS Safari background suspend) re-fires `/api/auth/me/access`
   *  and feels like the app is "constantly refreshing". Throttle to
   *  one visibility-driven refresh per 30s per tab — the routing
   *  decision derived from `access` only flips after operator-side
   *  changes, which are rare relative to focus events.
   */
  const _lastVisibilityRefreshAtRef = useRef(0);
  const VISIBILITY_REFRESH_FLOOR_MS = 30_000;
  useEffect(() => {
    if (!user || !hasProfile) return undefined;
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      if ((now - _lastVisibilityRefreshAtRef.current)
          < VISIBILITY_REFRESH_FLOOR_MS) {
        return;
      }
      _lastVisibilityRefreshAtRef.current = now;
      reload();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [user, hasProfile, reload]);

  const allowedWorkspaces = useMemo(
    () => (access?.allowed_workspaces || []),
    [access],
  );
  const effectiveDefault = access?.effective_default || null;

  const currentWorkspace = useMemo(
    () => workspaceFromPath(location.pathname, effectiveDefault),
    [location.pathname, effectiveDefault],
  );

  /** Switch workspaces: persist the new default server-side (if
   *  different) and navigate to the landing path. No-op when `next`
   *  is not in `allowedWorkspaces`.
   */
  const switchWorkspace = useCallback(async (next) => {
    if (!next || !allowedWorkspaces.includes(next)) return;
    navigate(landingPathFor(next));
    if (!access) return;
    if (access.default_workspace === next) return;
    if (lastPersistedRef.current === next) return;
    lastPersistedRef.current = next;
    try {
      const updated = await apiFetch('/api/auth/me/default-workspace', {
        method: 'POST',
        body: { default_workspace: next },
      });
      setAccess(updated || null);
    } catch (err) {
      // Don't block the navigation on a persistence failure — next
      // login will still land somewhere they have access to.
      // eslint-disable-next-line no-console
      console.warn('[WorkspaceContext] default_workspace persist failed:', err?.message || err);
    }
  }, [access, allowedWorkspaces, navigate]);

  const value = useMemo(() => ({
    access, loading, error,
    allowedWorkspaces, effectiveDefault, currentWorkspace,
    reload, switchWorkspace,
  }), [
    access, loading, error,
    allowedWorkspaces, effectiveDefault, currentWorkspace,
    reload, switchWorkspace,
  ]);

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
}

export function useWorkspace() {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) {
    throw new Error('useWorkspace must be used within WorkspaceProvider');
  }
  return ctx;
}
